import React, { useMemo } from "react"

import { type Table } from "../shared"

import { TS_STYLES } from "./styles"

type TableDropdownPanelProps = {
  tables: Table[]
  availableTableIds: Set<string>
  onSelect: (tableId: string) => void
}

type TableGroup = {
  id: string
  name: string
  tables: Table[]
}

/**
 * Grouped table list that opens upward from the table pill (Figma 1283-17212). Rows are grouped by
 * `tableGroup.name`; unavailable rows render at 40% opacity and are not selectable. Selecting a row
 * is handled by the parent (which also closes the panel).
 */
const TableDropdownPanel: React.FC<TableDropdownPanelProps> = ({ tables, availableTableIds, onSelect }) => {
  const groups = useMemo(() => {
    const byId = new Map<string, TableGroup>()
    tables.forEach((table) => {
      const groupId = table.tableGroup.id
      const existing = byId.get(groupId)
      if (existing) {
        existing.tables.push(table)
      } else {
        byId.set(groupId, { id: groupId, name: table.tableGroup.name, tables: [table] })
      }
    })
    return Array.from(byId.values())
  }, [tables])

  return (
    <div className={TS_STYLES.tablePanel} role="listbox" data-testid="public-reservation-modal-mobile-table-panel">
      {groups.map((group, groupIndex) => (
        <div key={group.id}>
          {groupIndex > 0 ? <div className={TS_STYLES.tableGroupDivider} /> : null}
          <div className={TS_STYLES.tableGroupHeader}>{group.name}</div>
          {group.tables.map((table) => {
            const isAvailable = availableTableIds.has(table.id)
            return (
              <button
                key={table.id}
                type="button"
                role="option"
                aria-selected={false}
                aria-disabled={!isAvailable}
                disabled={!isAvailable}
                className={`${TS_STYLES.tableRow} ${isAvailable ? "" : TS_STYLES.tableRowDisabled}`}
                onClick={() => isAvailable && onSelect(table.id)}
                data-testid={`public-reservation-modal-mobile-table-row-${table.id}`}
              >
                {table.name}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export default TableDropdownPanel
